import type {DataRouterApplicationBuilder} from '@github-ui/react-core/future/data-router-application'
import {mainQuery} from '@github-ui/react-core/future/main-query'
import {
  type RepoPullsDashboardContentResponse,
  RepoPullsDashboardContentResponseSchema,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/repo_pulls_dashboard'

import {pullRequestsAppBuilder} from '../config/app-builder'
import {
  type LegacyRepoPullsDashboardContentPayload,
  normalizeLegacyRepoPullsContent,
} from '../helpers/repo-pulls-dashboard-proto'

export const REPO_PULLS_DEFAULT_QUERY = 'is:pr state:open'

const CLEAN_PATH_FILTERS: Array<{pattern: RegExp; qualifier: string}> = [
  {pattern: /^created_by\/([^/]+)$/, qualifier: 'author'},
  {pattern: /^assigned\/([^/]+)$/, qualifier: 'assignee'},
  {pattern: /^mentioned\/([^/]+)$/, qualifier: 'mentions'},
  {pattern: /^review-requested\/([^/]+)$/, qualifier: 'review-requested'},
  {pattern: /^([^/]+)$/, qualifier: 'author'},
]

function cleanPathQualifier(pathname: string): string | undefined {
  const cleanPath = pathname.split('/pulls/')[1]
  if (!cleanPath) return undefined

  for (const {pattern, qualifier} of CLEAN_PATH_FILTERS) {
    const value = pattern.exec(cleanPath)?.[1]
    if (value) return `${qualifier}:${decodeURIComponent(value)}`
  }

  return undefined
}

export function repoPullsQueryResolver({
  pathname,
  searchParams,
}: {
  pathname: string
  searchParams: URLSearchParams
}): string {
  const query = searchParams.get('q')?.trim() || REPO_PULLS_DEFAULT_QUERY
  const qualifier = cleanPathQualifier(pathname)
  return qualifier ? `${query} ${qualifier}` : query
}

function repoPullsSearchParams(searchParams: URLSearchParams) {
  const params: Record<string, string> = {
    q: searchParams.get('q')?.trim() || REPO_PULLS_DEFAULT_QUERY,
    page: searchParams.get('page') || '1',
  }
  const milestone = searchParams.get('milestone')
  if (milestone) params.milestone = milestone
  return params
}

export function buildRepoPullsDashboardContentRoute(appBuilder: DataRouterApplicationBuilder<string>) {
  return appBuilder.createQueryRouteConfig('repoPullsDashboardContentRoute', {
    path: '/:owner/:repo/pulls',
    queries: [
      mainQuery<RepoPullsDashboardContentResponse>({
        schema: RepoPullsDashboardContentResponseSchema,
        adapt: legacy => normalizeLegacyRepoPullsContent(legacy as LegacyRepoPullsDashboardContentPayload),
        queryDeps: ({pathname, searchParams}) => ({
          pathname,
          searchParams: repoPullsSearchParams(searchParams),
        }),
      }),
    ],
  })
}

export const repoPullsDashboardContentRoute = buildRepoPullsDashboardContentRoute(pullRequestsAppBuilder)
